import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/common/Header';
import Footer from '../components/common/Footer';
import Spinner from '../components/common/Spinner';
import ArrowComponent from '../components/common/ArrowComponent';
import Button from '../components/common/Button';
import useScrollToTop from '../hooks/hook';
import { api } from '../config';
import { useError, withErrorHandling } from '../components/common/ErrorHandling';

// インターフェース定義
interface Option {
  id: number;
  optionText: string;
}

interface SurveyQuestion {
  id: number;
  questionText: string;
  category: string;
  options: Option[];
}

// API定義
const fetchSurveyQuestions = async (): Promise<SurveyQuestion[]> => {
  const response = await api.getQuestions();
  return response.map((q: any) => ({
    id: q.id,
    questionText: q.question_text,
    category: q.category,
    options: q.options.map((o: any) => ({
      id: o.id,
      optionText: o.option_text,
    })),
  }));
};

const postSurveyAnswers = async (formattedAnswers: any[]) => {
  await api.postResponses(formattedAnswers);
};

const HomePage: React.FC = () => {
  const [questions, setQuestions] = useState<SurveyQuestion[]>([]);
  const TOTAL_QUESTIONS = questions.length;
  const [answers, setAnswers] = useState<{ [key: number]: number }>({});
  const [selectedOptions, setSelectedOptions] = useState<{ [key: number]: number }>({});
  const userId = 'user_' + Math.random().toString(36).slice(2, 11);
  const [loading, setLoading] = useState<boolean>(true);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [fade, setFade] = useState<'fade-in' | 'fade-out'>('fade-in');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isLastQuestionAnswered, setIsLastQuestionAnswered] = useState(false);

  const { setError } = useError();
  const navigate = useNavigate();
  useScrollToTop();

  // 初期化処理
  useEffect(() => {
    const initializeSurvey = async () => {
      try {
        const data = await fetchSurveyQuestions();
        setQuestions(data);
      } catch (error) {
        setError('アンケートデータの取得中に問題が発生しました。再読み込みしてください。');
      } finally {
        setLoading(false);
      }
    };

    const handleWindowResize = () => setIsMobile(window.innerWidth < 768);

    initializeSurvey();
    window.addEventListener('resize', handleWindowResize);
    return () => window.removeEventListener('resize', handleWindowResize);
  }, []);

  // レンダリング関数群
  const render = {
    loadingSpinner: () => <Spinner />,

    questionTitle: (questionText: string) =>
      questionText.split('\n').map((text, index) => (
        <React.Fragment key={index}>
          {text}
          {index < questionText.split('\n').length - 1 && <br />}
        </React.Fragment>
      )),

    progressBar: () => (
      <div className="progress-bar-container">
        <div
          className="progress-bar"
          style={{ width: `${((currentQuestionIndex + 1) / TOTAL_QUESTIONS) * 100}%` }}
        />
      </div>
    ),

    prevButton: () => (
      <div className="navigation-buttons">
        {currentQuestionIndex > 0 && (
          <button className="btn nav-button" onClick={handlePrev}>
            前へ
          </button>
        )}
      </div>
    ),
  };

  // 再レンダリング関数群
  const refresh = {
    currentQuestion: () => {
      if (isLastQuestionAnswered) {
        return (
          <div className="question">
            <Button onClick={handleSubmit} className="submit-button" iconType="right" iconSize={30}>
              分析結果を見る
            </Button>
          </div>
        );
      }

      const currentQuestion = questions[currentQuestionIndex];
      if (!currentQuestion) return null;

      return (
        <div className="question">
          <div className="l_question_title">
            <div className="l_question_title_number">
              <p className="question_title_number">{`Q${currentQuestionIndex + 1}`}</p>
            </div>
            <p className="question_title">{render.questionTitle(currentQuestion.questionText)}</p>
          </div>
          {currentQuestion.category === "人生を肯定するちから" ? (
            <div className="l_pt5_options">
              <div className="options pt5_options">
                {currentQuestion.options.map((option) => (
                  <label key={option.id} style={{ flexGrow: 1, textAlign: 'center', margin: '0 10px' }}>
                    <input
                      type="radio"
                      name={`question_${currentQuestion.id}`}
                      onClick={() => handleOptionChange(currentQuestion.id, option.id)}
                      checked={selectedOptions[currentQuestion.id] === option.id}
                      style={{ display: 'none' }}
                    />
                    <span className={`custom-radio`}></span>
                  </label>
                ))}
              </div>
              <div className="arrow-container">
                <p className="option-text">{currentQuestion.options[0].optionText}</p>
                <ArrowComponent isMobile={isMobile} />
                <p className="option-text">{currentQuestion.options[currentQuestion.options.length - 1].optionText}</p>
              </div>
            </div>
          ) : (
            <div className="options">
              {currentQuestion.options.map((option) => (
                <label key={option.id}>
                  <input
                    type="radio"
                    name={`question_${currentQuestion.id}`}
                    onClick={() => handleOptionChange(currentQuestion.id, option.id)}
                    checked={selectedOptions[currentQuestion.id] === option.id}
                  />
                  <span className={`custom-radio`}></span>
                  <p className="option-text">
                    {render.questionTitle(option.optionText)}
                  </p>
                </label>
              ))}
            </div>
          )}
        </div>
      );
    },
  };

  // イベントハンドラ
  const handleOptionChange = (questionId: number, optionId: number) => {
    setSelectedOptions((prev) => ({ ...prev, [questionId]: optionId }));
    setAnswers((prev) => ({ ...prev, [questionId]: optionId }));

    if (currentQuestionIndex >= TOTAL_QUESTIONS - 1) {
      setIsLastQuestionAnswered(true);
    } else if (currentQuestionIndex < TOTAL_QUESTIONS - 1) {
      setFade('fade-out');
      setTimeout(() => {
        setCurrentQuestionIndex((prevIndex) => {
          const nextIndex = Math.min(prevIndex + 1, TOTAL_QUESTIONS - 1);
          return nextIndex;
        });
        setFade('fade-in');
      }, 500);
    }
  };

  const handlePrev = () => {
    if (isLastQuestionAnswered) {
      setFade('fade-out');
      setTimeout(() => {
        setFade('fade-in');
        setIsLastQuestionAnswered(false);
      }, 500);
    } else {
      if (currentQuestionIndex > 0 && currentQuestionIndex < TOTAL_QUESTIONS) {
        setFade('fade-out');
        setTimeout(() => {
          setCurrentQuestionIndex((prevIndex) => {
            const nextIndex = Math.max(prevIndex - 1, 0);
            return nextIndex;
          });
          setFade('fade-in');
          setIsLastQuestionAnswered(false);
        }, 500);
      }
    }
  }

  const handleSubmit = async () => {
    const formattedAnswers = Object.entries(answers).map(([questionId, selectedOptionId]) => ({
      questionId: Number(questionId),
      selectedOptionId,
      userId,
    }));

    try {
      await postSurveyAnswers(formattedAnswers);
      navigate('/results', { state: { userId } });
    } catch (error) {
      setError('アンケート回答の送信中に問題が発生しました。再読み込みしてください。');
    }
  };


  // メイン描画
  return (
    <div className="l_container">
      <Header />
      {loading ? render.loadingSpinner() : (
        <div className="survey-container">
          <div className={`survey-form ${fade}`}>{refresh.currentQuestion()}</div>
          {render.progressBar()}
          {render.prevButton()}
        </div>
      )}
      <Footer />
    </div>
  );
};

export default HomePage;
